import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {LandingPage} from '../../../../../models/landing-page/landing-page';
import {LandingPageService} from '../../../../../services/landing-page/landing-page.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {forkJoin} from 'rxjs';
import {Offer} from '../../../../../models/offer';
import {OfferService} from '../../../../../services/offer/offer.service';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {DropdownSelectComponent} from '../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {IsGranted} from '../../../../../decorator/is-granted';
import {Site} from '../../../../../models/site/site';
import {SiteService} from '../../../../../services/site/site.service';
import {PageTypeService} from '../../../../../services/page-type/page-type.service';
import {PageType} from '../../../../../models/page-type';

@Component({
  selector: 'app-landing-page-form',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    DropdownSelectComponent,
    DropdownSelectOptionComponent
  ],
  templateUrl: './landing-page-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './landing-page-form.component.scss'
})
@IsGranted(['LANDING_PAGE_VIEW'])
@MetaTitle('Promotion link form')
export class LandingPageFormComponent implements OnInit {
  loaded = false;
  id: number | null = null;
  landingPage: LandingPage | null = null;
  offers: Offer[] = [];
  pageTypes: PageType[] = [];
  variables: string[] = [];
  sites: Site[] = [];

  form = new FormGroup({
    name: new FormControl('', Validators.required),
    url: new FormControl('', Validators.required),
    enabled: new FormControl(true),
    external: new FormControl(false),
    offers: new FormControl<number[]>([], Validators.required),
    typeId: new FormControl<number | null>(null, Validators.required),
    siteId: new FormControl<number | null>(null),
  });

  constructor(
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private landingPageService: LandingPageService,
    private offerService: OfferService,
    private pageTypeService: PageTypeService,
    private siteService: SiteService,
  ) {
  }

  ngOnInit(): void {
    this.id = Number(this.route.snapshot.paramMap.get('id'));

    this.loader.startLoading();

    forkJoin([
      this.offerService.findAll(),
      this.pageTypeService.findAll({sort: 'name'}),
      this.landingPageService.fetchVariables(),
      this.siteService.findAll({sort: 'name'}),
    ]).subscribe(([offers, pageTypes, variables, sites]) => {
      this.offers = offers;
      this.pageTypes = pageTypes;
      this.variables = variables;
      this.sites = sites;

      if (this.id) {
        this.landingPageService.findOneById(this.id, {groups: LandingPage.DETAILS_GROUP}).subscribe((landingPage: LandingPage) => {
          this.landingPage = landingPage;
          this.form.patchValue({
            name: this.landingPage.name,
            url: this.landingPage.url,
            external: this.landingPage.external,
            enabled: this.landingPage.enabled,
            offers: this.landingPage.offers,
            typeId: this.landingPage.type?.id ?? null,
            siteId: this.landingPage.siteId,
          });
          this.loader.stopLoading();
          this.loaded = true;
        });
      } else {
        this.loader.stopLoading();
        this.loaded = true;
      }
    });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loader.startLoading();

    if (this.isCreate()) {
      this.landingPageService.create(this.form.value).subscribe(() => {
        this.loader.stopLoading();
        this.router.navigateByUrl('promotion-tools/landing-pages').then(() => {
          this.snackbar.success('The promotion link has been created');
        });
      });
    } else if (this.landingPage?.id) {
      this.landingPageService.update(this.landingPage.id, this.form.value).subscribe(() => {
        this.loader.stopLoading();
        this.router.navigateByUrl('promotion-tools/landing-pages').then(() => {
          this.snackbar.success('Promotion link has been modified.');
        });
      });
    }
  }

  isCreate(): boolean {
    return !this.id;
  }

  getUrl(): string | null {
    return this.form.get('url')?.value ?? null;
  }

  insertVariable(variable: string): void {
    let currentUrl = this.form.get('url')?.value || '';
    const suffixedVariable = '{' + variable + '}';

    if (currentUrl.includes(suffixedVariable)) {
      currentUrl = currentUrl.replace(suffixedVariable, '');
    } else {
      currentUrl += suffixedVariable;
    }

    this.form.patchValue({url: currentUrl});
  }

  insertEncryptedVariable(variable: string): void {
    return this.insertVariable('enc:' + variable);
  }
}
